import type { U2000Condition, U2000ConditionType } from '../components/tables/basic/U2000BasicSearch';
import type { U2000TableColumn } from '../components/tables/basic/U2000BasicTableList';
import { U2000_CRITERIA_LOOKUP_U3134_FIND_INVOICE } from '../components/tables/commons/search/U2000CriteriaLookupRegistry';
import type { Criteria, U2000SearchStateFilter } from '../reducer/formReducer/U2000SearchState';
import { u2000SortByLocalizedLabel } from './u2000SortByLocalizedLabel';

/** Opérateurs par défaut pour toute colonne `criteriaLookup` (BL-0404). */
export const U2000_CRITERIA_LOOKUP_DEFAULT_OPERATORS: readonly string[] = ['equals', 'in'];

/**
 * Colonnes filtrées et triées par libellé affiché (BL-0110).
 */
export function u2000SortColumnsByLabel(
  columns: U2000TableColumn[],
  locale: string,
  predicate: (col: U2000TableColumn) => boolean,
): U2000TableColumn[] {
  return u2000SortByLocalizedLabel(
    columns.filter((col) => col && predicate(col)),
    (col) => col.label,
    (col) => col.name,
    locale,
  );
}

/** Colonnes critère (onglet Critères). */
export function u2000SortCriteriaColumns(columns: U2000TableColumn[], locale: string): U2000TableColumn[] {
  return u2000SortColumnsByLabel(columns, locale, (col) => col.isCriteriaField === true);
}

/** Colonnes triables (onglet Tri par — liste disponible). */
export function u2000SortSortableColumns(columns: U2000TableColumn[], locale: string): U2000TableColumn[] {
  return u2000SortColumnsByLabel(columns, locale, (col) => col.sortable === true);
}

/** Colonnes grille (onglet Colonnes — liste disponible). */
export function u2000SortGridColumns(columns: U2000TableColumn[], locale: string): U2000TableColumn[] {
  return u2000SortColumnsByLabel(columns, locale, (col) => col.inGridField === true);
}

/** Exemption BL-0404 : critère facture U3134 conserve isNull / isNotNull. */
export function isCriteriaLookupOperatorsExempt(column: U2000TableColumn): boolean {
  return column.criteriaLookup?.findMethod === U2000_CRITERIA_LOOKUP_U3134_FIND_INVOICE;
}

/**
 * Whitelist effective d’opérateurs pour une colonne.
 * Lookup hors exemption → toujours equals + in (BL-0404).
 */
export function resolveEffectiveIncludeConditionsForColumn(column: U2000TableColumn): string[] | undefined {
  if (column.criteriaLookup != null && !isCriteriaLookupOperatorsExempt(column)) {
    return [...U2000_CRITERIA_LOOKUP_DEFAULT_OPERATORS];
  }
  return column.includeConditions;
}

/**
 * Si l’opérateur n’est plus autorisé pour un lookup, bascule silencieusement vers `equals`
 * (conserve value / displayValue côté appelant).
 */
export function u2000CoerceLookupCriteriaCondition(
  field: U2000TableColumn,
  condition: U2000Condition,
  conditionTypes?: U2000ConditionType[],
  locale: string = 'fr',
): U2000Condition {
  if (field.criteriaLookup == null) {
    return condition;
  }
  const allowedNames = resolveEffectiveIncludeConditionsForColumn(field)
    ?? field.includeConditions
    ?? [...U2000_CRITERIA_LOOKUP_DEFAULT_OPERATORS];
  if (allowedNames.includes(condition.name)) {
    return condition;
  }
  if (conditionTypes) {
    const resolved = resolveFieldConditionsForColumn(field, conditionTypes, locale);
    const equals = resolved?.find((c) => c.name === 'equals');
    if (equals) {
      return { ...equals };
    }
    if (resolved?.[0]) {
      return { ...resolved[0] };
    }
  }
  return {
    ...condition,
    name: 'equals',
    isRequiredValue: true,
  };
}

/** Coercition des critères d’un filtre sauvegardé (BL-0404). */
export function u2000CoerceFilterLookupOperators(
  filter: U2000SearchStateFilter,
  conditionTypes: U2000ConditionType[],
  locale: string,
): U2000SearchStateFilter {
  return {
    ...filter,
    criterias: (filter.criterias ?? []).map((c: Criteria) => ({
      ...c,
      condition: u2000CoerceLookupCriteriaCondition(c.field, c.condition, conditionTypes, locale),
    })),
  };
}

/**
 * Opérateurs autorisés pour une colonne critère, avec overrides de libellé et tri alphabétique (BL-0110).
 * BL-0404 : colonnes criteriaLookup hors exemption → equals + in uniquement.
 */
export function resolveFieldConditionsForColumn(
  column: U2000TableColumn,
  conditionTypes: U2000ConditionType[],
  locale: string,
): U2000Condition[] | null {
  const conditionType = conditionTypes.find((cond) => cond.type === column.type);
  if (!conditionType) {
    return null;
  }

  const effectiveInclude = resolveEffectiveIncludeConditionsForColumn(column);

  let excludeConditions = column.excludeConditions ?? [];
  if (effectiveInclude !== undefined) {
    excludeConditions = conditionType.conditions
      .filter((c) => !effectiveInclude.includes(c.name))
      .map((c) => c.name);
  }

  const fieldConditions = conditionType.conditions
    .filter((c) => !excludeConditions.includes(c.name))
    .map((c) => ({ ...c }));

  if (column.ovverideLabelConditions !== undefined) {
    column.ovverideLabelConditions.forEach((override) => {
      const cond = fieldConditions.find((cc) => cc.name === override.name);
      if (cond) {
        cond.label = override.label;
      }
    });
  }

  return u2000SortByLocalizedLabel(fieldConditions, (c) => c.label, (c) => c.name, locale);
}
